/**
 * Text Input Component
 * ===================
 * 
 * Form input template component for rendering text input fields with validation and formatting.
 * This component is designed to be used within individual form components to provide
 * a consistent text input interface with support for various input types and auto-formatting.
 * 
 * Core Features:
 * - Multiple input types (text, email, tel, etc.)
 * - Automatic phone number formatting
 * - Form validation integration with error display
 * - Autocomplete and autocapitalize support
 * - Customizable styling and placeholder text
 * - Touch state management for validation display
 * 
 * Input Properties:
 * - templateFormGroup: FormGroup containing the form controls
 * - control: AbstractControl for the specific text field
 * - label: Field label with required indicator
 * - type: Input type (text, email, tel, etc.)
 * - placeholder: Custom placeholder text
 * - autoComplete: Autocomplete behavior setting
 * - autoCapitalize: Autocapitalization behavior setting
 * 
 * Validation Features:
 * - Required field validation
 * - Email format validation
 * - Phone number format validation
 * - Length constraints (min/max)
 * - Pattern validation support
 * - Custom error messages for validation failures
 * 
 * Special Features:
 * - Phone number auto-formatting to (xxx) xxx-xxxx format
 * - Blur event handling for formatting
 * - Enter key support for immediate formatting
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-text-input',
  template: `
    <ng-container
      *ngIf="templateFormGroup && control"
      [formGroup]="templateFormGroup"
    >
    <ion-label [position]="position" class="form-label">
       <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>
      <ion-item class="ion-no-padding" lines="none" class="form-input-border">

        <ion-input
          [id]="controlName"
          #customInput
          [formControl]="formControl"
          [type]="type"
          [fill]="fill"
          [name]="type === 'tel' ? 'tel' : controlName"
          [autocomplete]="autoComplete"
          [autocapitalize]="autoCapitalize"
          [placeholder]="placeholder"
          [class]="inputClass"
          [class.ion-invalid]="hasError"
          [class.ion-touched]="control.touched"
          [errorText]="getErrorMessage()"
          (ionBlur)="onBlur($event)"
          (keydown.enter)="onEnter($event)"
          
        >

        </ion-input>

      </ion-item>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      /* Optional: Style invalid inputs */
      ion-input.ion-invalid.ion-touched {
        --border-color: var(--ion-color-danger);
        --color: var(--ion-color-danger);
      }
    `,
  ],
})
export class TextInputComponent implements OnChanges, OnInit {
  @Input() templateFormGroup!: FormGroup;
  @Input() control!: AbstractControl | null;
  @Input() label!: string;
  @Input() requiredField: boolean = false;
  @Input() type: string = 'text';
  @Input() fill: string = 'none';
  @Input() placeholder: string = '';
@Input() autoComplete: string = 'on';
@Input() autoCapitalize: string = 'on';
  @Input() inputClass: string = 'custom-input';
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'floating';


  controlName: string = '';
  formControl: FormControl = new FormControl();
  name!: string;

  /**
   * Computed property to check if control has errors and is touched.
   * Used to determine when to show error states and styling.
   * 
   * @returns True if the control has validation errors and has been touched
   */
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  /**
   * Gets the appropriate error message based on validation errors.
   * Provides user-friendly error messages for different validation failures.
   * 
   * @returns String containing the appropriate error message
   */
  getErrorMessage(): string {
    if (!this.hasError || !this.control?.errors) {
      return '';
    }

    const errors = this.control.errors;

    if (errors['required']) {
      return `${this.label} is required`;
    }

    if (errors['phoneNumber']) {
      return errors['phoneNumber']?.message || 'Please enter a valid phone number';
    }

    if (errors['email']) {
      return 'Please enter a valid email address';
    }

    if (errors['minlength']) {
      return `${this.label} must be at least ${errors['minlength'].requiredLength} characters`;
    }

    if (errors['maxlength']) {
      return `${this.label} must be no more than ${errors['maxlength'].requiredLength} characters`;
    }

    if (errors['pattern']) {
      return `${this.label} format is invalid`;
    }

    // Default error message
    return `${this.label} is invalid`;
  }

  /**
   * Handles blur event to auto-format phone numbers.
   * Automatically formats phone numbers to (xxx) xxx-xxxx format when the field loses focus.
   * Only applies to telephone input types.
   * 
   * @param event - The blur event object
   */
  onBlur(event: any): void {
    if (this.type === 'tel' && event.target.value) {
      const formattedValue = this.formatPhoneNumber(event.target.value);
      if (formattedValue !== event.target.value) {
        this.formControl.setValue(formattedValue);
      }
    }
  }

  /**
   * Handles Enter key press to trigger immediate formatting.
   * Calls the blur handler when Enter is pressed to format phone numbers immediately.
   * 
   * @param event - The keydown event object
   */
  onEnter(event: any): void {
    if (event.key === 'Enter') {
      this.onBlur(event);
    }
  }

  /**
   * Formats phone number to (xxx) xxx-xxxx format.
   * Removes all non-digit characters and applies standard US phone number formatting.
   * Only formats if exactly 10 digits are present.
   * 
   * @param value - Raw phone number input string
   * @returns Formatted phone number string or original value if formatting cannot be applied
   * @private
   */
  private formatPhoneNumber(value: string): string {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');

    // Only format if we have exactly 10 digits (US phone number)
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(
        3,
        6
      )}-${digitsOnly.slice(6, 10)}`;
    }

    // If not 10 digits, return original value (let validation handle it)
    return value;
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the control name and initializes the component with appropriate name attribute.
   */
  ngOnInit(): void {
    if (this.control) {
      // Get the control name from the form group
      this.controlName =
        Object.keys(this.templateFormGroup.controls).find(
          (key) => this.templateFormGroup.get(key) === this.control
        ) || '';
    }
    switch (this.type) {
      case 'tel':
        this.name = 'tel';
        break;
      case 'email':
        this.name = 'email';
        break;
      default:
        this.name = this.controlName;
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles changes to templateFormGroup and control properties.
   * Updates the form control reference when the control changes.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('templateFormGroup' in changes) {
      this.templateFormGroup = changes['templateFormGroup'].currentValue;
    }
    if ('control' in changes && this.control) {
      this.controlName =
        Object.keys(this.templateFormGroup.controls).find(
          (key) => this.templateFormGroup.get(key) === this.control
        ) || '';
      this.formControl = this.control as FormControl;
    }
  }
}
